/**
 * 솜니아 슬립케어 결제 처리 (PortOne browser-sdk + Node `GET /api/sleep-care/payment/:id` + Supabase checkouts)
 *
 * - 결제 완료 확인: `routes/sleepCarePortone.js` (서버 PORTONE_API_SECRET)
 * - 앱 결제용 `/api/payment/complete`(routes/payment.js, apply_checkout) 는 슬립케어에서 쓰지 않음
 * DB 스키마: public.checkouts
 */
import PortOne from '@portone/browser-sdk/v2';
import { supabase } from '../lib/supabaseClient';
import { fetchPortOnePayment } from '../lib/portoneRestClient';
import {
  parsePortOneCustomData,
  validateSleepCarePortOnePayment,
} from '../lib/sleepCarePaymentValidate';
import { PORTONE_CHANNEL_KEY, PORTONE_STORE_ID, SLEEP_CARE_ORDER_NAME } from './portoneSleepCare';
import { COHORT_CARE_OPTION_IDS } from './sleepCareConstants';

export { COHORT_CARE_OPTION_IDS };

export const APP_BASENAME = '/somnia-sleepcare';
/** PortOne redirect 후 돌아올 경로 (basename 이하) */
export const CHECKOUT_RETURN_PATH = '/checkout';

export const PRICING_PROMO_CODE = 'sleepcare';
export const PORTONE_DUMMY_PHONE = '01000000000';

const PENDING_SESSION_KEY = 'sc_sleepcare_checkout_pending';

export function isPaidCheckoutRow(row) {
  const st = (row.payment_status || '').toUpperCase();
  return st === 'PAID' || st === 'COMPLETED';
}

/** 환불·취소 등으로 해당 건이 더 이상 ‘유효 결제(재결제 막음)’가 아닌지 */
export function isCheckoutRefundedOrVoided(row) {
  const ps = (row.payment_status || '').toUpperCase();
  if (ps === 'REFUNDED' || ps === 'CANCELLED') return true;
  const rs = (row.refund_status || '').toUpperCase();
  if (['COMPLETED', 'FULL', 'REFUNDED', 'DONE', 'SUCCESS'].includes(rs)) return true;
  const amt = row.payment_amount != null ? Number(row.payment_amount) : null;
  const ref = row.refund_amount != null ? Number(row.refund_amount) : null;
  if (amt != null && ref != null && !Number.isNaN(amt) && !Number.isNaN(ref) && ref >= amt) {
    return true;
  }
  return false;
}

/** 기수당 1건 제한용: 결제 완료이면서 아직 환불 등으로 끊기지 않은 건 */
export function isActivePaidEnrollment(row) {
  return isPaidCheckoutRow(row) && !isCheckoutRefundedOrVoided(row);
}

export function randomPaymentId() {
  return [...crypto.getRandomValues(new Uint32Array(2))]
    .map((w) => w.toString(16).padStart(8, '0'))
    .join('');
}

/** redirectUrl: 결제 완료 후 돌아올 전체 URL (쿼리는 PG/PortOne이 paymentId 등을 붙임) */
export function buildSleepCareCheckoutRedirectUrl(customerName, customerEmail, customerPhone) {
  const redirectParams = new URLSearchParams({
    customerName,
    customerEmail,
    customerPhone,
  });
  return `${window.location.origin}${APP_BASENAME}${CHECKOUT_RETURN_PATH}?${redirectParams.toString()}`;
}

/** redirect 결제 직전에 호출 — 돌아온 뒤 checkouts insert 시 케어·프로모 정보 복원 */
export function storeSleepCareCheckoutPending(ctx) {
  sessionStorage.setItem(PENDING_SESSION_KEY, JSON.stringify(ctx));
}

export function peekSleepCareCheckoutPending() {
  const raw = sessionStorage.getItem(PENDING_SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function consumeSleepCareCheckoutPending() {
  const p = peekSleepCareCheckoutPending();
  if (p) sessionStorage.removeItem(PENDING_SESSION_KEY);
  return p;
}

export function clearSleepCareCheckoutPending() {
  sessionStorage.removeItem(PENDING_SESSION_KEY);
}

export async function fetchCohortCheckoutPaid(cohortIds = null) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user?.id) return false;

  let activeOptionsQuery = supabase.from('cohort_care_options').select('id').eq('is_active', true);
  if (Array.isArray(cohortIds) && cohortIds.length) {
    activeOptionsQuery = activeOptionsQuery.in('id', cohortIds);
  }
  const { data: activeOptions, error: activeOptionsError } = await activeOptionsQuery;

  if (activeOptionsError) {
    console.error('[cohort_care_options]', activeOptionsError);
    return false;
  }

  const activeCohortIdSet = new Set((activeOptions || []).map((row) => row.id));
  if (!activeCohortIdSet.size) return false;

  let { data, error } = await supabase
    .from('checkouts')
    .select('cohort_care_option_id, payment_status, refund_status, refund_amount, payment_amount')
    .eq('auth_user_id', session.user.id);

  if (error) {
    const fallback = await supabase
      .from('checkouts')
      .select(
        'cohort_care_option_id, payment_status, refund_status, refund_amount, payment_amount'
      );
    if (fallback.error) {
      console.error('[checkouts]', error, fallback.error);
      return false;
    }
    data = fallback.data;
  }

  return (data || []).some(
    (row) =>
      isActivePaidEnrollment(row) &&
      row.cohort_care_option_id &&
      activeCohortIdSet.has(row.cohort_care_option_id)
  );
}

export function buildSleepCareCustomData({
  hasPreOrderMatch,
  careTypeIndex,
  cohortCareOptionId,
  totalAmount,
  customerName,
}) {
  return {
    product: 'sleepcare',
    preorderPromo: hasPreOrderMatch,
    promoCode: hasPreOrderMatch ? PRICING_PROMO_CODE : '',
    careTypeIndex,
    cohortCareOptionId,
    expectedAmount: String(totalAmount),
    customerName: encodeURIComponent(customerName),
  };
}

export async function requestSleepCarePortOnePayment({
  paymentId,
  totalAmount,
  customerName,
  customerEmail,
  customerPhone,
  customData,
  redirectUrl,
}) {
  return PortOne.requestPayment({
    storeId: PORTONE_STORE_ID,
    channelKey: PORTONE_CHANNEL_KEY,
    paymentId,
    orderName: SLEEP_CARE_ORDER_NAME,
    totalAmount,
    currency: 'KRW',
    payMethod: 'CARD',
    customer: {
      fullName: customerName,
      email: customerEmail,
      phoneNumber: customerPhone,
    },
    customData,
    redirectUrl,
  });
}

function mapCardFields(payment) {
  const m = payment.method;
  if (m && m.type === 'PaymentMethodCard') {
    return {
      card_company: m.card?.publisher ?? null,
      card_number_masked: m.card?.number ?? null,
      installment_month: m.installment?.month ?? 0,
    };
  }
  return { card_company: null, card_number_masked: null, installment_month: 0 };
}

/**
 * PortOne 결제 단건 조회 결과로 public.checkouts insert
 */
export async function insertCheckoutFromPortOnePayment(
  payment,
  { authUserId, customerName, customerEmail, customerPhone }
) {
  const cd = parsePortOneCustomData(payment.customData) || {};
  const { card_company, card_number_masked, installment_month } = mapCardFields(payment);

  const payload = {
    auth_user_id: authUserId,
    payment_id: payment.id,
    transaction_id: payment.pgTxId || payment.transactionId || null,
    order_name: payment.orderName,
    payment_method: 'CARD',
    payment_amount: payment.amount.total,
    currency: payment.currency,
    payment_status: 'PAID',
    paid_at: payment.paidAt,
    is_paid: true,
    card_company,
    card_number_masked,
    installment_month,
    preorder_promo_applied: !!cd.preorderPromo,
    raw_custom_data: cd,
    cohort_care_option_id: cd.cohortCareOptionId ?? null,
  };

  const { error } = await supabase.from('checkouts').insert(payload);
  if (error) {
    if (error.code === '23505') return;
    console.error('[checkouts insert]', error);
    throw error;
  }
}

/**
 * 결제 완료 후: `GET /api/sleep-care/payment/:paymentId` 로 조회 → 검증 → Supabase checkouts insert
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
export async function finalizeSleepCarePaymentSuccess({
  paymentId,
  customerName,
  customerEmail,
  customerPhone,
  authUserId,
  totalAmount,
}) {
  let payment;
  try {
    payment = await fetchPortOnePayment(paymentId);
  } catch (e) {
    console.error(e);
    return {
      success: false,
      message:
        e.message ||
        'PortOne 결제 조회에 실패했습니다. CRA와 API 포트가 다르면 REACT_APP_SOMNI_API_BASE_URL(백엔드 origin)을 설정하세요.',
    };
  }

  const v = validateSleepCarePortOnePayment(payment, { customerEmail });
  if (!v.ok) {
    return { success: false, message: v.message };
  }

  if (totalAmount != null && payment.amount?.total !== totalAmount) {
    return { success: false, message: '결제 금액이 요청과 일치하지 않습니다.' };
  }

  try {
    await insertCheckoutFromPortOnePayment(payment, {
      authUserId,
      customerName,
      customerEmail,
      customerPhone,
    });
  } catch (e) {
    console.error(e);
    return {
      success: false,
      message: '결제는 확인되었으나 DB 저장에 실패했습니다. 고객센터로 문의해 주세요.',
    };
  }

  return { success: true };
}

function extractRefundSnapshot(payment) {
  if (!payment || typeof payment !== 'object') return null;

  const refunds = Array.isArray(payment.refunds) ? payment.refunds : [];
  const lastRefund = refunds.length ? refunds[refunds.length - 1] : null;

  const rawRefundAmount =
    payment.refundAmount ??
    payment.amount?.cancelled ??
    payment.amount?.refunded ??
    payment.amount?.refund ??
    lastRefund?.amount ??
    null;
  const refundAmount = rawRefundAmount != null ? Number(rawRefundAmount) : null;
  const paymentAmount = payment.amount?.total != null ? Number(payment.amount.total) : null;
  const isFullRefund =
    refundAmount != null &&
    paymentAmount != null &&
    !Number.isNaN(refundAmount) &&
    !Number.isNaN(paymentAmount) &&
    refundAmount >= paymentAmount;

  const statusUpper = String(payment.status || '').toUpperCase();
  const refundStatusRaw =
    payment.refundStatus ||
    payment.cancellationStatus ||
    lastRefund?.status ||
    (refundAmount != null ? (isFullRefund ? 'COMPLETED' : 'PARTIAL') : null);
  const refundStatus = refundStatusRaw ? String(refundStatusRaw).toUpperCase() : null;

  const refundedAtRaw =
    payment.cancelledAt || payment.refundedAt || lastRefund?.cancelledAt || null;
  const refundReason = lastRefund?.reason || payment.cancelReason || payment.refundReason || null;

  const hasRefundSignal =
    statusUpper === 'CANCELLED' ||
    statusUpper === 'REFUNDED' ||
    refundAmount != null ||
    !!refundStatus;

  if (!hasRefundSignal) return null;

  return {
    payment_status:
      statusUpper === 'CANCELLED' || statusUpper === 'REFUNDED'
        ? statusUpper
        : isFullRefund
          ? 'REFUNDED'
          : payment.status || null,
    is_paid: !isFullRefund,
    refund_status: refundStatus,
    refund_amount: refundAmount != null && !Number.isNaN(refundAmount) ? refundAmount : null,
    refunded_at: refundedAtRaw || null,
    refund_reason: refundReason || null,
  };
}

/**
 * 결제내역 페이지 진입 시 PortOne 환불 상태를 동기화
 * - 환불/취소 신호가 있는 건만 checkouts 업데이트
 * @returns {Promise<{ok: boolean, updated: number, failed: number}>}
 */
export async function syncSleepCareRefundStatuses(authUserId) {
  if (!authUserId) return { ok: false, updated: 0, failed: 0 };

  const { data: rows, error } = await supabase
    .from('checkouts')
    .select('payment_id, payment_status')
    .eq('auth_user_id', authUserId)
    .not('payment_id', 'is', null);

  if (error) {
    console.error('[checkouts sync select]', error);
    return { ok: false, updated: 0, failed: 0 };
  }

  let updated = 0;
  let failed = 0;

  for (const row of rows || []) {
    try {
      const payment = await fetchPortOnePayment(row.payment_id);
      const refundPatch = extractRefundSnapshot(payment);
      if (!refundPatch) continue;

      const { error: updateError } = await supabase
        .from('checkouts')
        .update(refundPatch)
        .eq('payment_id', row.payment_id)
        .eq('auth_user_id', authUserId);
      if (updateError) {
        failed += 1;
        console.error('[checkouts sync update]', row.payment_id, updateError);
        continue;
      }
      updated += 1;
    } catch (e) {
      failed += 1;
      console.error('[checkouts sync portone]', row.payment_id, e);
    }
  }

  return { ok: true, updated, failed };
}

function buildFailureReason(portoneCode, message, paymentStatus) {
  const parts = [portoneCode, message, paymentStatus].filter(Boolean);
  return parts.length ? parts.join(' | ') : 'CANCELLED';
}

/**
 * PortOne 결제 취소·실패 시 public.checkouts 에 반영 (단건 조회 결과 또는 pending 기준)
 * @returns {Promise<{ ok: boolean, skipped?: boolean }>}
 */
export async function recordSleepCareCheckoutCancelledFromPortOne({
  paymentId,
  authUserId,
  customerName,
  customerEmail,
  customerPhone,
  pending = {},
  portoneCode,
  message,
}) {
  if (!paymentId || !String(paymentId).trim()) return { ok: false };

  let payment = null;
  try {
    payment = await fetchPortOnePayment(paymentId);
  } catch {
    /* 조회 실패 시 pending 만으로 진행 */
  }

  if (payment) {
    const st = String(payment.status || '').toUpperCase();
    if (st === 'PAID' || st === 'COMPLETED') return { ok: false, skipped: true };
  }

  const cdFromPayment = payment ? parsePortOneCustomData(payment.customData) : null;
  const cdFromPending =
    pending.rawCustomData && typeof pending.rawCustomData === 'object'
      ? pending.rawCustomData
      : parsePortOneCustomData(pending.rawCustomData);
  const cd = cdFromPayment || cdFromPending || {};

  const statusFromServer = payment?.status ? String(payment.status).toUpperCase() : '';
  const paymentStatus =
    statusFromServer || (portoneCode === 'PORTONE_USER_CANCELLED' ? 'CANCELLED' : 'FAILED');

  const amount = payment?.amount?.total ?? pending.totalAmount ?? pending.total_amount ?? null;

  const payload = {
    auth_user_id: authUserId,
    payment_id: payment?.id || paymentId,
    transaction_id: payment?.pgTxId || payment?.transactionId || null,
    order_name: payment?.orderName || SLEEP_CARE_ORDER_NAME,
    payment_method: 'CARD',
    payment_amount: amount,
    currency: payment?.currency || 'KRW',
    payment_status: paymentStatus,
    paid_at: payment?.paidAt || null,
    is_paid: false,
    payment_failed_reason: buildFailureReason(portoneCode, message, paymentStatus),
    card_company: null,
    card_number_masked: null,
    installment_month: 0,
    preorder_promo_applied: !!(cd.preorderPromo ?? pending.preorderPromoApplied),
    raw_custom_data: cd,
    cohort_care_option_id: cd.cohortCareOptionId ?? pending.cohortCareOptionId ?? null,
  };

  const { error } = await supabase.from('checkouts').insert(payload);
  if (error) {
    if (error.code === '23505') return { ok: true };
    console.error('[checkouts cancel/fail insert]', error);
    return { ok: false };
  }
  return { ok: true };
}
